import { useMemo, useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Archive, Delete, LayersFront, Link, Save } from '@factorialco/f0-react/icons/app'
import type { EngagementSurveysListPageDataCollectionQuery } from 'generated/resources/graphql'
import {
  EngagementSurveysListPageDataCollectionDocument,
  FormsEnumsStatusEnum,
  SaveSurveyAsTemplateDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useActivities } from 'modules/core/lib/activities'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { getFormStatusFilter } from 'modules/engagement_surveys/lib/filters'
import { copyInboxTaskLink } from 'modules/forms/lib/shareLink'
import {
  formRecurrenceMapper,
  mapRecurrenceTypeFromBackend,
} from 'modules/engagement_surveys/lib/formRecurrenceMapper'
import { formStatusMapper } from 'modules/engagement_surveys/lib/formStatusMapper'

type SurveyNode =
  EngagementSurveysListPageDataCollectionQuery['surveys']['surveysConnection']['edges'][number]['node']

const DEFAULT_SURVEY_STATUS_FILTERS: FormsEnumsStatusEnum[] = [
  'active',
  'draft',
  'scheduled',
  'finished',
]

const useSurveysSource = ({
  handleCreateSurvey,
  onCanCreateSurveyChange,
}: {
  handleCreateSurvey: () => void
  onCanCreateSurveyChange: (value: boolean) => void
}) => {
  const { navigation } = useNavigation()
  const { openActivity } = useActivities()
  const [canCreateSurvey, setCanCreateSurvey] = useState(false)

  const [saveAsTemplate] = useMutation(SaveSurveyAsTemplateDocument, {
    extractorFn: (data) => data.formsMutations.duplicateFormsForm,
  })

  const source = useGraphqlDataCollectionSource({
    document: EngagementSurveysListPageDataCollectionDocument,
    mapData: (data) => {
      const can = !!data.surveys?._policies?.surveysCreateSurvey?.can
      setCanCreateSurvey(can)
      onCanCreateSurveyChange(can)
      return data.surveys.surveysConnection.edges.map((edge) => edge.node)
    },
    mapPagination: (data) => ({
      total: Number(data.surveys.surveysConnection.totalCount),
    }),
    mapVariables: ({ filters, search }) => ({
      status:
        filters.status && filters.status.length > 0
          ? filters.status.filter(Boolean)
          : DEFAULT_SURVEY_STATUS_FILTERS,
      search,
    }),
    search: {
      enabled: true,
    },
    filters: {
      status: getFormStatusFilter(),
    },
    itemUrl(item) {
      return navigation.engagement.smart_surveys.survey.fullPath({
        surveyId: item.id,
      })
    },
    primaryActions: () => {
      if (canCreateSurvey) {
        return {
          label: i18n.t('surveys.surveys_table.actions.create'),
          icon: Add,
          onClick: handleCreateSurvey,
        }
      }

      return undefined
    },
    itemActions: ({ id, form }) => {
      const actions = []

      if (form.status === 'active' || form.status === 'scheduled') {
        actions.push({
          label: i18n.t('surveys.link'),
          icon: Link,
          onClick: () => {
            const inboxPath = navigation.inbox.todo.fullPath()
            copyInboxTaskLink(inboxPath, 'Forms::Form', form.id)
          },
        })
      }

      if (form.status === 'finished') {
        actions.push({
          label: i18n.t('common.archive'),
          icon: Archive,
          onClick: () => {
            openActivity('engagement_surveys.archiveSurvey', { surveyId: id.toString() })
          },
        })

        actions.push({ type: 'separator' as const })
      }

      actions.push({
        label: i18n.t('surveys.save_as_template'),
        icon: Save,
        onClick: async () => {
          const result = await saveAsTemplate({
            variables: {
              formId: form.id,
              duplicateName: form.title,
            },
          })

          const handled = handleResult(result, () => null)

          if (handled.success) {
            app.setFlash({
              type: 'correct',
              message: i18n.t('common.saved_successfully'),
            })
          }
        },
      })

      if (canCreateSurvey) {
        actions.push({
          label: i18n.t('common.duplicate'),
          icon: LayersFront,
          onClick: () => {
            openActivity('engagement_surveys.duplicateSurvey', { surveyId: id.toString() })
          },
        })
      }

      if (form.status === 'draft') {
        actions.push({
          critical: true,
          label: i18n.t('common.delete'),
          icon: Delete,
          onClick: () => {
            openActivity('engagement_surveys.deleteSurvey', { surveyId: id.toString() })
          },
        })
      }

      return actions
    },
  })

  return { source, canCreateSurvey }
}

const useSurveysColumns = () =>
  useMemo(
    () => [
      {
        label: i18n.t('surveys.surveys_table.columns.title'),
        render: (item: SurveyNode) => item.form.title,
      },
      {
        label: i18n.t('surveys.surveys_table.columns.status'),
        render: (item: SurveyNode) => ({
          type: 'status' as const,
          value: formStatusMapper(item.form.status),
        }),
      },
      {
        label: i18n.t('surveys.surveys_table.columns.questions'),
        render: (item: SurveyNode) => item.form.questionsCount?.toString() ?? '-',
      },
      {
        label: i18n.t('surveys.surveys_table.columns.recurrence'),
        render: (item: SurveyNode) => {
          const recurrenceType =
            item.form.recurrenceType ?? item.form.schedule?.recurrenceType ?? null
          return formRecurrenceMapper(mapRecurrenceTypeFromBackend(recurrenceType))
        },
      },
      {
        label: i18n.t('surveys.surveys_table.columns.participation'),
        render: (item: SurveyNode) => {
          if (['scheduled', 'draft'].includes(item.form.status)) {
            return '-'
          }

          const sent = Number(item.form.sentCount)
          const answered = Number(item.form.answeredCount)
          const percentage = sent > 0 ? Number(((answered / sent) * 100).toFixed(1)) : 0

          return {
            type: 'percentage' as const,
            value: {
              label: `${answered}/${sent}`,
              percentage,
            },
          }
        },
      },
    ],
    []
  )

const useSurveysEmptyStates = ({
  canCreateSurvey,
  handleCreateSurvey,
}: {
  canCreateSurvey: boolean
  handleCreateSurvey: () => void
}) =>
  useMemo(
    () => ({
      'no-data': {
        title: i18n.t('surveys.surveys_table.empty_states.no_data.title'),
        description: i18n.t('surveys.surveys_table.empty_states.no_data.description'),
        actions: canCreateSurvey
          ? [
              {
                label: i18n.t('surveys.surveys_table.empty_states.no_data.create_action'),
                onClick: handleCreateSurvey,
              },
            ]
          : undefined,
      },
    }),
    [canCreateSurvey, handleCreateSurvey]
  )

export const SurveysDataCollection = ({
  handleCreateSurvey,
  onCanCreateSurveyChange,
}: {
  handleCreateSurvey: () => void
  onCanCreateSurveyChange: (value: boolean) => void
}) => {
  const { source, canCreateSurvey } = useSurveysSource({
    handleCreateSurvey,
    onCanCreateSurveyChange,
  })
  const columns = useSurveysColumns()
  const emptyStates = useSurveysEmptyStates({ canCreateSurvey, handleCreateSurvey })

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: { columns },
        },
      ]}
      emptyStates={emptyStates}
    />
  )
}
